import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS_STYLE = {
  completed: "bg-emerald-500/15 text-emerald-700",
  waiting: "bg-violet-500/15 text-violet-700",
  stopped_by_filter: "bg-sky-500/15 text-sky-700",
  error: "bg-destructive/15 text-destructive",
  running: "bg-primary/15 text-primary",
};

export default function AutomationRuns() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/automations/runs/all?limit=200");
      setRuns(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load runs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="auto-runs-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/app/automation" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground" data-testid="runs-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Automations
          </Link>
          <h1 className="font-display text-4xl mt-1">Run history</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every automation execution across your workspace — succeeded, waiting, or aborted by a filter.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full gap-1.5" data-testid="runs-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : runs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/40 p-10 text-center text-muted-foreground">
          <Clock className="h-6 w-6 mx-auto mb-2 opacity-60" />
          No automation runs yet.
        </div>
      ) : (
        <div className="space-y-3">
          {runs.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-4" data-testid={`run-item-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()} · event: {r.event}
                  </div>
                  <div className="mt-1 font-medium truncate">{r.automation_name || "(unnamed)"}</div>
                  {r.context?.lead?.business_name && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Lead: <span className="text-foreground/80">{r.context.lead.business_name}</span>
                    </div>
                  )}
                </div>
                <span className={"text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full " + (STATUS_STYLE[r.status] || "bg-secondary")}>
                  {r.status}
                </span>
              </div>
              {(r.node_traces || []).length > 0 && (
                <div className="mt-3 grid gap-1.5">
                  {r.node_traces.map((t, i) => (
                    <div key={i} className="text-xs flex items-start gap-2">
                      <span className="opacity-40 pt-0.5">·</span>
                      <div>
                        <span className="text-muted-foreground">{t.kind}</span>{" "}
                        <span className={
                          t.status === "ok" ? "text-emerald-600" :
                          t.status === "waiting" ? "text-violet-600" :
                          t.status === "stopped" ? "text-sky-600" : "text-destructive"
                        }>{t.status}</span>
                        {t.message && <span className="text-muted-foreground"> — {t.message}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {r.resume_at && (
                <div className="mt-2 text-[11px] text-violet-600">
                  Waiting to resume at {new Date(r.resume_at).toLocaleString()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
